import { GenerateRebalancingPlanCombinedParams } from '@cast/types';

import {
  useGenerateKentRebalancePlanMutation,
  useGenerateRebalancePlanMutation,
} from 'hooks/mutations/rebalancer';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { useIsKentRebalancingMode } from './useIsKentRebalancingMode';
import { mapCombinedCreateRebalancingPlanParamsToKentPlanParams } from '../_utils/kentUtils';
import { mapCombinedCreateRebalancingPlanParamsToRegularPlanParams } from '../_utils/mappers';
import { useCallback } from 'react';

export const useCorrectGenerateRebalancePlanMutation = (clusterId: string) => {
  const { currentOrganization } = useOrganizations();
  const shouldUseKentApi = useIsKentRebalancingMode();

  const regularMutation = useGenerateRebalancePlanMutation(clusterId);

  const kentMutation = useGenerateKentRebalancePlanMutation(clusterId);

  const mutate = (
    params: GenerateRebalancingPlanCombinedParams,
    onSuccess: (id?: string) => void
  ) => {
    if (shouldUseKentApi) {
      kentMutation.mutate(
        mapCombinedCreateRebalancingPlanParamsToKentPlanParams(
          params,
          clusterId,
          currentOrganization?.id ?? ''
        ),
        {
          onSuccess: ({ data }) => {
            onSuccess(data.id);
          },
        }
      );
    } else {
      regularMutation.mutate(
        mapCombinedCreateRebalancingPlanParamsToRegularPlanParams(params),
        {
          onSuccess: ({ data }) => {
            onSuccess(data.rebalancingPlanId);
          },
        }
      );
    }
  };

  const mutateAsync = useCallback(
    async (
      params: GenerateRebalancingPlanCombinedParams
    ): Promise<{ rebalancingPlanId?: string }> => {
      if (shouldUseKentApi) {
        const response = await kentMutation.mutateAsync(
          mapCombinedCreateRebalancingPlanParamsToKentPlanParams(
            params,
            clusterId,
            currentOrganization?.id ?? ''
          )
        );
        return { rebalancingPlanId: response.data.id };
      }
      const response = await regularMutation.mutateAsync(params);
      return { rebalancingPlanId: response.data.rebalancingPlanId };
    },
    [
      shouldUseKentApi,
      kentMutation,
      regularMutation,
      clusterId,
      currentOrganization?.id,
    ]
  );

  return shouldUseKentApi
    ? { ...kentMutation, mutate, mutateAsync }
    : { ...regularMutation, mutate, mutateAsync };
};
